import { useMemo } from 'react';
import { toggle } from './useUrlFilters.js';

// T270(5)/T161 — the twelve filter writers, with stable identities.
//
// They were twelve inline arrows at the <AvailableJobsTable> call site, which
// meant twelve new function identities on every render of a 2600-line
// component — and therefore that memoising the hundred-row table was
// impossible no matter how the props were grouped. Grouping alone would have
// been cosmetic; this is the part that makes the grouping worth anything.
//
// The comment this replaces claimed "useUrlFilters rebuilds the Set on every
// render". Measured, it does not: `value` is memoised on `[params]`, so across
// three renders that do not touch the URL there is exactly one Set identity and
// one setValue identity. The workaround that belief produced — deriving string
// keys instead of depending on the Sets — is still in the page and can go
// whenever someone wants it; it is simply not load-bearing.
//
// `filters` is the object useUrlFilters returns, already memoised, so this memo
// re-runs only when the URL actually changes.
export function useFilterActions(filters, setFilters) {
  const {
    filterSources, filterCities, filterCountries,
    filterRemote, filterSeniority, filterCompany,
  } = filters;

  return useMemo(() => ({
    // `replace: true` — a keystroke must not stack a history entry.
    onSearchChange: (q) => setFilters({ searchQ: q }, { replace: true }),
    onToggleSource: (s) => setFilters({ filterSources: toggle(filterSources, s) }),
    onToggleCity: (c) => setFilters({ filterCities: toggle(filterCities, c) }),
    onToggleCountry: (c) => setFilters({ filterCountries: toggle(filterCountries, c) }),
    onToggleRemote: (v) => setFilters({ filterRemote: toggle(filterRemote, v) }),
    onToggleSeniority: (v) => setFilters({ filterSeniority: toggle(filterSeniority, v) }),
    onToggleCompany: (v) => setFilters({ filterCompany: toggle(filterCompany, v) }),
    onSetScoreBand: (v) => setFilters({ filterScoreBand: v }),
    onSetAi: (v) => setFilters({ filterAi: v }),
    onSetDateBand: (v) => setFilters({ filterDateBand: v }),
    onSetSort: (key, dir) => setFilters({ sortKey: key, sortDir: dir }),
  }), [
    setFilters,
    filterSources, filterCities, filterCountries,
    filterRemote, filterSeniority, filterCompany,
  ]);
}
